///
/// EarthBrowser
///
/// Created by Matt Giger on 10/25/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Translucent walls and floor of the cutout box below sea level. Which
/// two walls draw follows the camera each frame (the far pair), so the box
/// never occludes its own contents; the shader draws the grid, depth ticks
/// and frame.
///

import { boxDepthKm, type CutoutLayout } from "./cutoutLayout"
import { NodeBase } from "@stovokor/engine/NodeBase"
import { Renderer } from "@stovokor/engine/Renderer"
import { type RenderState } from "@stovokor/engine/RenderState"
import { type cube } from "@stovokor/math/cube"
import { type matrix } from "@stovokor/math/matrix"
import { type sphere } from "@stovokor/math/sphere"
import { vec4 } from "@stovokor/math/vector"
import { type Geometry } from "@stovokor/shader/Geometry"
import { DrawMode } from "@stovokor/shader/GLConstants"
import { IndexBuffer } from "@stovokor/shader/IndexBuffer"
import { type Material } from "@stovokor/shader/Material"
import { ShaderSourceDefinition } from "@stovokor/shader/ShaderSourceCode"
import { UniformDynamic } from "@stovokor/shader/Uniform"
import { VertexAttribute } from "@stovokor/shader/VertexAttribute"
import { VertexBuffer } from "@stovokor/shader/VertexBuffer"
import { type StovokorContext } from "@stovokor/StovokorContext"

/** Which side of each axis the camera is on, +1 or -1 */
export interface FarWalls {
	/** Camera sign along x; the far x wall is at -signX * halfX */
	signX: number
	/** Camera sign along z; the far z wall is at -signZ * halfZ */
	signZ: number
}

export class CutoutWallsNode extends NodeBase {
	private readonly wallsRenderer: CutoutWallsRenderer

	constructor(context: StovokorContext, layout: CutoutLayout) {
		super()
		const geometry = new CutoutWallsGeometry(context, layout)
		this.wallsRenderer = new CutoutWallsRenderer(context, geometry)
		this.wallsRenderer.halfX.value = layout.lengthKm / 2
		this.wallsRenderer.halfZ.value = layout.widthKm / 2
		this.wallsRenderer.depthFloor.value = boxDepthKm(layout)
		this.wallsRenderer.tick.value = layout.tickKm
		this.wallsRenderer.depthTick.value = layout.tickKm * layout.depthScale
		this.renderer = this.wallsRenderer
	}

	public set opacity(value: number) {
		this.wallsRenderer.opacity.value = value
	}

	/** Far-wall selection decided by the parent from the camera position */
	public setFarWalls(far: FarWalls): void {
		this.wallsRenderer.visibleWalls.value = new vec4(
			far.signX > 0 ? 1 : 0,	// wall 0 at -halfX is far when the camera is at +x
			far.signX > 0 ? 0 : 1,
			far.signZ > 0 ? 1 : 0,
			far.signZ > 0 ? 0 : 1
		)
	}
}

class CutoutWallsRenderer extends Renderer {
	public modelViewProjection = new UniformDynamic<matrix>()
	public visibleWalls = new UniformDynamic<vec4>(new vec4(1, 0, 1, 0))
	public halfX = new UniformDynamic<number>(1)
	public halfZ = new UniformDynamic<number>(1)
	public depthFloor = new UniformDynamic<number>(1)
	public tick = new UniformDynamic<number>(1)
	public depthTick = new UniformDynamic<number>(1)
	public opacity = new UniformDynamic<number>(0)

	constructor(context: StovokorContext, geometry: Geometry) {
		const transparentMaterial: Material = { transparency: true, available: true }
		super(context, new ShaderSourceDefinition("cutout_wall"), geometry, [transparentMaterial])
		// Far walls are seen from inside the box
		this.drawState.cullFace = false
	}

	protected stateUpdate(state: RenderState): void {
		this.modelViewProjection.value = state.modelViewProjection
	}
}

class CutoutWallsGeometry implements Geometry {
	public position: VertexAttribute
	public wallId: VertexAttribute
	public indexBuffer: IndexBuffer
	public vertexBuffer: VertexBuffer
	public mode: DrawMode = DrawMode.Triangles
	public bounds: cube
	public boundingSphere: sphere
	public available: boolean = true

	constructor(context: StovokorContext, layout: CutoutLayout) {
		this.position = new VertexAttribute(context, 3)
		this.wallId = new VertexAttribute(context, 1)
		this.indexBuffer = new IndexBuffer(context)
		this.vertexBuffer = new VertexBuffer(context, [this.position, this.wallId])

		const hx = layout.lengthKm / 2
		const hz = layout.widthKm / 2
		const floor = -boxDepthKm(layout)

		const quad = (id: number, a: number[], b: number[], c: number[], d: number[]): void => {
			const base = this.position.count
			for(const p of [a, b, c, d]) {
				this.position.addPos(p[0], p[1], p[2])
				this.wallId.add(id)
			}
			this.indexBuffer.quad(base, base + 1, base + 2, base + 3)
		}
		quad(0, [-hx, 0, -hz], [-hx, 0, hz], [-hx, floor, hz], [-hx, floor, -hz])
		quad(1, [hx, 0, -hz], [hx, 0, hz], [hx, floor, hz], [hx, floor, -hz])
		quad(2, [-hx, 0, -hz], [hx, 0, -hz], [hx, floor, -hz], [-hx, floor, -hz])
		quad(3, [-hx, 0, hz], [hx, 0, hz], [hx, floor, hz], [-hx, floor, hz])
		quad(4, [-hx, floor, -hz], [hx, floor, -hz], [hx, floor, hz], [-hx, floor, hz])

		this.bounds = this.position.bounds
		this.boundingSphere = this.bounds.sphere
	}

	public destroy(): void {
		this.vertexBuffer.destroy()
		this.indexBuffer.destroy()
	}
}
